import { api } from '../console/api.ts';
import {
  carrierCell,
  carrierNames,
  type PlanNames,
  planName,
  setPlanNames,
} from '../console/carriers.ts';
import { empty, field, pill } from '../console/components.ts';
import { InputError } from '../console/errors.ts';
import { date, esc, fmt, icon, lineName, type Money } from '../console/format.ts';
import { type Actions, current, render } from '../console/router.ts';
import { $, act, toast } from '../console/ui.ts';
import { checkboxControl, selectControl, textareaControl, textControl } from '../controls.ts';

/**
 * «Planes y coberturas» and the carrier's profile (#307, ADR-0037), inside «Productos y
 * aseguradoras»: the plans of the agency's carriers per product line, each with its coverages
 * (limit, deductible, whether it is mandatory, included or optional, and «simulado» where the
 * carrier has not confirmed the figure), two to four plans side by side, the agency's settings of
 * a plan (management), and each carrier's profile: contacts, portals, the agent code, and for
 * management the account to remit to and the commission agreement in force.
 */

export type CoverageLimit =
  | { kind: 'FIXED'; amount: Money }
  | { kind: 'RANGE'; min: Money; max: Money }
  | { kind: 'SUM_INSURED'; basis: string }
  | { kind: 'DESCRIBED'; text: string };
export type PlanCoverage = {
  code: string;
  carrierCode: string;
  name: string;
  description: string | null;
  kind: 'MANDATORY' | 'INCLUDED' | 'OPTIONAL';
  limit: CoverageLimit;
  deductible: string | null;
  coinsurance: string | null;
  exclusions: string[];
  simulated: boolean;
};
export type PlanSummary = {
  key: string;
  carrier: string;
  carrierName: string;
  version: string;
  productLine: string;
  code: string;
  name: string;
  displayName: string;
  description: string | null;
  currencies: string[];
  coverages: number;
  simulated: boolean;
  hidden: boolean;
};
export type PlanDetail = Omit<PlanSummary, 'coverages'> & {
  validFrom: string | null;
  validTo: string | null;
  acceptance: { text: string; simulated: boolean }[];
  territory: string | null;
  conditionsDocument: string | null;
  coverages: PlanCoverage[];
  settings: { hidden: boolean; commercialName: string | null; note: string | null } | null;
  configurable: boolean;
};
type Catalog = {
  carrier: string;
  carrierName: string;
  version: string;
  source: string;
  importedAt: string;
};
type Comparison = {
  productLine: string;
  plans: PlanSummary[];
  rows: { key: string; name: string; cells: (PlanCoverage | null)[] }[];
};
type Contact = { area: string; name: string; email: string | null; phone: string | null };
type Account = { bank: string; holder: string; number: string; type: string; currency: string };
type AgreementLine = {
  productLine: string;
  plans: {
    code: string;
    rate: string | null;
    effectiveFrom: string | null;
    next: { rate: string; effectiveFrom: string } | null;
  }[];
};
export type CarrierProfile = {
  carrier: string;
  carrierName: string;
  agentCode: string | null;
  catalog: { version: string; source: string; importedAt: string } | null;
  contacts: Contact[];
  portals: { label: string; url: string }[];
  /** Absent: the person may not see it (not «none registered»). */
  remittanceAccount?: Account | null;
  agreement?: AgreementLine[];
  updatedAt: string | null;
  editable: boolean;
};

export const KIND: Record<PlanCoverage['kind'], { label: string; cls: string }> = {
  MANDATORY: { label: 'Obligatoria', cls: 'info' },
  INCLUDED: { label: 'Incluida', cls: 'ok' },
  OPTIONAL: { label: 'Opcional', cls: 'DRAFT' },
};
export const AREA: Record<string, string> = {
  ISSUANCE: 'Emisión',
  COLLECTIONS: 'Cobros',
  CLAIMS: 'Siniestros',
  SALES: 'Comercial',
};
const ACCOUNT_TYPE: Record<string, string> = { MONETARY: 'Monetaria', SAVINGS: 'Ahorro' };
const CURRENCY: Record<string, string> = { GTQ: 'Quetzales', USD: 'Dólares' };
const SIMULATED_HINT =
  'La aseguradora no publica esta cifra: es un parámetro del simulador, por confirmar';
export const simulatedChip = (on: boolean) =>
  on ? ` <span class="chip sim" title="${SIMULATED_HINT}">simulado</span>` : '';

/** A coverage's limit as the agency reads it: «Q200,000.00», «Valor del vehículo». */
export function limitText(l: CoverageLimit): string {
  switch (l.kind) {
    case 'FIXED':
      return fmt(l.amount);
    case 'RANGE':
      return `${fmt(l.min)} a ${fmt(l.max)}`;
    case 'SUM_INSURED':
      return l.basis;
    default:
      return l.text;
  }
}
const kindPill = (k: PlanCoverage['kind']) => pill(KIND[k].cls, KIND[k].label);
const deductibleText = (c: PlanCoverage) =>
  c.deductible ? esc(c.deductible) : '<span class="muted">No publicado</span>';

// ------------------------------------------------------------------ state

/** The product line on view, the plans ticked to compare, what is open. */
let line: string | null = null;
let picked: string[] = [];
let detail: PlanDetail | null = null;
let comparison: Comparison | null = null;
/** The carrier whose profile is being edited (null: none). */
let editing: string | null = null;

/** Forgets the choices of the person before (a new session starts with the list). */
export function resetPlans() {
  line = null;
  picked = [];
  detail = null;
  comparison = null;
  editing = null;
}

// ------------------------------------------------------------------ plans

const pathOf = (key: string) => key.split(':').map(encodeURIComponent).join('/');

function planRow(p: PlanSummary) {
  const open = detail?.key === p.key;
  const name =
    p.displayName !== p.name
      ? `<strong>${esc(p.displayName)}</strong><div class="muted small">Plan de la aseguradora: ${esc(p.name)}</div>`
      : `<strong>${esc(p.name)}</strong>`;
  return `<tr data-plan="${esc(p.key)}"${open ? ' class="selected"' : ''}>
    <td class="pick">${checkboxControl(
      {
        'data-plan-pick': p.key,
        checked: picked.includes(p.key),
        disabled: !picked.includes(p.key) && picked.length >= 4,
      },
      `<span class="sr-only">Comparar ${esc(p.displayName)}</span>`,
    )}</td>
    <td data-label="Plan">${name}${p.hidden ? ` ${pill('CLOSED', 'Oculto a los agentes')}` : ''}${p.description ? `<div class="muted small">${esc(p.description)}</div>` : ''}</td>
    <td data-label="Aseguradora">${carrierCell(p.carrier, p.carrierName)}</td>
    <td data-label="Moneda">${esc(p.currencies.join(' · '))}</td>
    <td data-label="Coberturas" class="num">${p.coverages}${p.simulated ? `<div>${simulatedChip(true).trim()}</div>` : ''}</td>
    <td class="actions"><button type="button" class="btn sm${open ? ' primary' : ''}" data-plan-open="${esc(p.key)}" aria-expanded="${open}" aria-controls="plan-detail">${open ? 'Cerrar' : 'Ver coberturas'}</button></td>
  </tr>`;
}

function coverageRows(list: PlanCoverage[]) {
  return list
    .map(
      (c) => `<tr data-coverage="${esc(c.carrierCode)}">
      <td data-label="Cobertura"><strong>${esc(c.name)}</strong>${c.description ? `<div class="muted small">${esc(c.description)}</div>` : ''}${c.exclusions.length ? `<div class="small exclusions">Excluye: ${esc(c.exclusions.join('; '))}</div>` : ''}</td>
      <td data-label="Tipo">${kindPill(c.kind)}</td>
      <td data-label="Suma asegurada o límite" class="num">${esc(limitText(c.limit))}${simulatedChip(c.simulated)}</td>
      <td data-label="Deducible">${deductibleText(c)}</td>
      <td data-label="Coaseguro">${c.coinsurance ? esc(c.coinsurance) : '<span class="muted">—</span>'}</td>
    </tr>`,
    )
    .join('');
}

const validity = (p: PlanDetail) =>
  p.validFrom || p.validTo
    ? `${p.validFrom ? `desde el ${date(p.validFrom)}` : ''}${p.validTo ? ` hasta el ${date(p.validTo)}` : ''}`.trim()
    : 'Vigente, sin fecha de fin';

export function planDetailView(p: PlanDetail) {
  const simulated = p.coverages.some((c) => c.simulated) || p.acceptance.some((a) => a.simulated);
  const settings = p.configurable
    ? `<form id="plan-settings-form" class="plan-settings" novalidate data-key="${esc(p.key)}">
        <h3 class="sub">Ajustes de la agencia</h3>
        <div class="form-grid">
          ${field('Nombre comercial', textControl({ id: 'plan-commercial', value: p.settings?.commercialName ?? '', maxlength: 120, placeholder: p.name, hint: 'Cómo lo vende la agencia; vacío: el de la aseguradora' }))}
          ${field('Nota para los agentes', textareaControl({ id: 'plan-note', value: p.settings?.note ?? '', rows: 2, maxlength: 500 }))}
        </div>
        ${checkboxControl({ id: 'plan-hidden', checked: p.settings?.hidden ?? false }, 'Ocultar este plan a los agentes')}
        <div class="actions-row"><button type="submit" class="btn primary" id="plan-settings-save">Guardar ajustes</button></div>
      </form>`
    : '';
  return `<section class="card plan-detail" id="plan-detail" tabindex="-1" aria-labelledby="plan-detail-title" data-component="plan-detail">
    <div class="card-head"><h2 id="plan-detail-title">${esc(p.displayName)}</h2><span class="spacer"></span>
      ${p.hidden ? pill('CLOSED', 'Oculto a los agentes') : ''}
      <button type="button" class="btn sm" data-plan-close aria-label="Cerrar las coberturas de ${esc(p.displayName)}">Cerrar</button></div>
    <dl class="plan-meta small">
      <div><dt>Aseguradora</dt><dd>${carrierCell(p.carrier, p.carrierName)}</dd></div>
      <div><dt>Ramo</dt><dd>${esc(lineName(p.productLine))}</dd></div>
      <div><dt>Código del plan</dt><dd class="mono">${esc(p.code)}</dd></div>
      <div><dt>Moneda</dt><dd>${esc(p.currencies.map((c) => CURRENCY[c] ?? c).join(' o '))}</dd></div>
      <div><dt>Vigencia del plan</dt><dd>${esc(validity(p))}</dd></div>
      <div><dt>Territorio</dt><dd>${p.territory ? esc(p.territory) : '<span class="muted">No indicado</span>'}</dd></div>
      <div><dt>Condicionado</dt><dd>${p.conditionsDocument ? `${esc(p.conditionsDocument)} <span class="muted">· en Conocimiento</span>` : '<span class="muted">La aseguradora no lo ha entregado</span>'}</dd></div>
      <div><dt>Catálogo</dt><dd>versión <span class="mono">${esc(p.version)}</span></dd></div>
    </dl>
    ${p.description ? `<p>${esc(p.description)}</p>` : ''}
    ${p.settings?.note ? `<p class="note info">${icon('alert')}<span><strong>Nota de la agencia:</strong> ${esc(p.settings.note)}</span></p>` : ''}
    ${
      p.acceptance.length
        ? `<h3 class="sub">Condiciones de aceptación</h3><ul class="plan-acceptance">${p.acceptance
            .map((a) => `<li>${esc(a.text)}${simulatedChip(a.simulated)}</li>`)
            .join('')}</ul>`
        : ''
    }
    <h3 class="sub">Coberturas</h3>
    <div class="table-wrap" data-component="table"><table class="table coverages stack">
      <thead><tr><th>Cobertura</th><th>Tipo</th><th class="num">Suma asegurada o límite</th><th>Deducible</th><th>Coaseguro</th></tr></thead>
      <tbody>${coverageRows(p.coverages)}</tbody></table></div>
    ${simulated ? '<p class="muted small policy-note">«simulado»: la aseguradora no publica esa cifra; es un parámetro del simulador, por confirmar con ella.</p>' : ''}
    ${settings}
  </section>`;
}

function compareCell(c: PlanCoverage | null) {
  if (!c) return '<span class="muted">No incluye</span>';
  return `${kindPill(c.kind)}<div class="premium">${esc(limitText(c.limit))}${simulatedChip(c.simulated)}</div><div class="muted small">Deducible: ${c.deductible ? esc(c.deductible) : 'no publicado'}</div>`;
}

export function comparisonView(t: Comparison) {
  return `<section class="card plan-compare" id="plan-compare" tabindex="-1" aria-labelledby="plan-compare-title" data-component="plan-compare">
    <div class="card-head"><h2 id="plan-compare-title">Comparación de planes · ${esc(lineName(t.productLine))}</h2><span class="spacer"></span>
      <button type="button" class="btn sm" data-plan-compare-close>Cerrar la comparación</button></div>
    <div class="table-wrap" data-component="table"><table class="table compare">
      <caption class="sr-only">Coberturas en filas, planes en columnas</caption>
      <thead><tr><th scope="col">Cobertura</th>${t.plans
        .map(
          (p) =>
            `<th scope="col">${esc(p.displayName)}<div class="muted small">${esc(p.carrierName)}</div></th>`,
        )
        .join('')}</tr></thead>
      <tbody>${t.rows
        .map(
          (r) =>
            `<tr data-row="${esc(r.key)}"><th scope="row">${esc(r.name)}</th>${r.cells
              .map(
                (c, i) =>
                  `<td data-label="${esc(t.plans[i]?.displayName ?? '')}">${compareCell(c)}</td>`,
              )
              .join('')}</tr>`,
        )
        .join('')}</tbody></table></div>
  </section>`;
}

/** The section «Planes y coberturas»: every plan of the agency's carriers, per product line. */
export async function plansSection(): Promise<string> {
  const r = await api<{ catalogs: Catalog[]; plans: PlanSummary[] }>('GET', '/v1/catalog/plans');
  if (!r.plans.length)
    return `<section class="card" id="planes"><div class="card-head"><h2>Planes y coberturas</h2></div>${empty(
      'layers',
      'Sin planes todavía',
      'Cuando una aseguradora de la agencia declare su catálogo, aquí verás sus planes con sus coberturas.',
    )}</section>`;
  const lines = [...new Set(r.plans.map((p) => p.productLine))];
  if (!line || !lines.includes(line)) line = lines[0] as string;
  const shown = r.plans.filter((p) => p.productLine === line);
  const tabs = lines
    .map(
      (l) =>
        `<button type="button" class="btn sm" role="tab" aria-selected="${l === line}" aria-pressed="${l === line}" aria-controls="plans-table" data-plan-line="${esc(l)}">${esc(lineName(l))}</button>`,
    )
    .join('');
  const versions = r.catalogs
    .map(
      (c) =>
        `<span class="muted small">Catálogo de ${esc(c.carrierName)} · versión <span class="mono">${esc(c.version)}</span> · importado el ${esc(date(c.importedAt))}</span>`,
    )
    .join('');
  const n = picked.length;
  const compare = `<button type="button" class="btn sm primary" data-plan-compare ${n >= 2 ? '' : 'aria-disabled="true"'} aria-describedby="plan-compare-hint">${icon('layers')}Comparar ${n ? `(${n})` : ''}</button>
    <span class="muted small" id="plan-compare-hint">${n >= 2 ? `${n} planes elegidos` : 'Elige de 2 a 4 planes del mismo ramo'}</span>`;
  return `<section class="card" id="planes" aria-labelledby="planes-title" data-component="plans">
    <div class="card-head"><h2 id="planes-title">Planes y coberturas</h2><span class="spacer"></span>${versions}</div>
    <p class="note info">${icon('alert')}<span>Cada aseguradora declara sus planes en su paquete de integración. Lo que dice <strong>simulado</strong> es un dato que la aseguradora no ha confirmado: no lo presentes al cliente como cifra de la aseguradora.</span></p>
    <div class="toolbar plan-toolbar"><div class="lead-filters" role="tablist" aria-label="Ramo">${tabs}</div><span class="spacer"></span>${compare}</div>
    <div class="table-wrap" data-component="table" id="plans-table"><table class="table plans stack">
      <thead><tr><th class="pick"><span class="sr-only">Comparar</span></th><th>Plan</th><th>Aseguradora</th><th>Moneda</th><th class="num">Coberturas</th><th class="actions"><span class="sr-only">Acción</span></th></tr></thead>
      <tbody>${shown.map(planRow).join('')}</tbody></table></div>
  </section>${comparison ? comparisonView(comparison) : ''}${detail ? planDetailView(detail) : ''}`;
}

// ------------------------------------------------------------------ the carrier's profile

function contactsView(list: Contact[], editable: boolean) {
  if (!list.length)
    return `<p class="muted small">Aún no hay contactos. ${editable ? 'Agrégalos con «Editar ficha».' : 'La dirección de la agencia los agrega.'}</p>`;
  return `<ul class="contacts">${list
    .map(
      (c) =>
        `<li><span class="chip">${esc(AREA[c.area] ?? c.area)}</span> <strong>${esc(c.name)}</strong>${c.email ? ` · <a href="mailto:${esc(c.email)}">${esc(c.email)}</a>` : ''}${c.phone ? ` · <a href="tel:${esc(c.phone.replace(/[^+0-9]/g, ''))}">${esc(c.phone)}</a>` : ''}</li>`,
    )
    .join('')}</ul>`;
}

function agreementView(lines: AgreementLine[], carrier: string) {
  if (!lines.length)
    return '<p class="muted small">La agencia no tiene tasas de comisión con esta aseguradora.</p>';
  const rows = lines.flatMap((l) =>
    l.plans.map(
      (
        p,
        i,
      ) => `<tr>${i === 0 ? `<th scope="rowgroup" rowspan="${l.plans.length}">${esc(lineName(l.productLine))}</th>` : ''}
        <td>${esc(planName(carrier, p.code, l.productLine))}</td>
        <td class="num">${p.rate === null ? '<span class="muted">—</span>' : `${esc(p.rate)} %`}</td>
        <td>${p.effectiveFrom ? esc(date(p.effectiveFrom)) : '<span class="muted">Aún no rige</span>'}</td>
        <td>${p.next ? `${esc(p.next.rate)} % desde el ${esc(date(p.next.effectiveFrom))}` : '<span class="muted">Sin cambios programados</span>'}</td></tr>`,
    ),
  );
  return `<div class="table-wrap" data-component="table"><table class="table agreement">
    <thead><tr><th>Ramo</th><th>Plan</th><th class="num">Tasa vigente</th><th>Rige desde</th><th>Próximo cambio</th></tr></thead>
    <tbody>${rows.join('')}</tbody></table></div>`;
}

function accountView(a: Account | null) {
  if (!a) return '<p class="muted small">Sin cuenta registrada.</p>';
  return `<dl class="plan-meta small">
    <div><dt>Banco</dt><dd>${esc(a.bank)}</dd></div>
    <div><dt>A nombre de</dt><dd>${esc(a.holder)}</dd></div>
    <div><dt>Número</dt><dd class="mono">${esc(a.number)}</dd></div>
    <div><dt>Tipo</dt><dd>${esc(ACCOUNT_TYPE[a.type] ?? a.type)} · ${esc(CURRENCY[a.currency] ?? a.currency)}</dd></div>
  </dl>`;
}

/** The form of a carrier's profile: one contact per area, the account, up to three portals. */
function profileForm(p: CarrierProfile) {
  const contact = (area: string) => p.contacts.find((c) => c.area === area);
  const portal = (i: number) => p.portals[i];
  const id = esc(p.carrier);
  const account = p.remittanceAccount ?? null;
  return `<form id="carrier-profile-form" class="carrier-profile-form" novalidate data-carrier="${id}">
    <h3 class="sub">Contactos por área</h3>
    ${Object.entries(AREA)
      .map(
        ([area, label]) => `<fieldset class="form-grid"><legend>${esc(label)}</legend>
        ${field('Nombre', textControl({ id: `pc-${area}-name`, value: contact(area)?.name ?? '', maxlength: 120 }))}
        ${field('Correo', textControl({ id: `pc-${area}-email`, type: 'email', value: contact(area)?.email ?? '', maxlength: 254 }))}
        ${field('Teléfono', textControl({ id: `pc-${area}-phone`, type: 'tel', value: contact(area)?.phone ?? '', maxlength: 20 }))}
      </fieldset>`,
      )
      .join('')}
    ${
      'remittanceAccount' in p
        ? `<h3 class="sub">Cuenta para remesas</h3><fieldset class="form-grid"><legend class="sr-only">Cuenta para remesas</legend>
      ${field('Banco', textControl({ id: 'pa-bank', value: account?.bank ?? '', maxlength: 80 }))}
      ${field('A nombre de', textControl({ id: 'pa-holder', value: account?.holder ?? '', maxlength: 120 }))}
      ${field('Número de cuenta', textControl({ id: 'pa-number', value: account?.number ?? '', maxlength: 30 }))}
      ${field(
        'Tipo',
        selectControl({ id: 'pa-type' }, [
          { value: 'MONETARY', label: 'Monetaria', selected: account?.type !== 'SAVINGS' },
          { value: 'SAVINGS', label: 'Ahorro', selected: account?.type === 'SAVINGS' },
        ]),
      )}
      ${field(
        'Moneda',
        selectControl({ id: 'pa-currency' }, [
          { value: 'GTQ', label: 'Quetzales', selected: account?.currency !== 'USD' },
          { value: 'USD', label: 'Dólares', selected: account?.currency === 'USD' },
        ]),
      )}
    </fieldset>`
        : ''
    }
    <h3 class="sub">Portales</h3>
    ${[0, 1, 2]
      .map(
        (i) => `<fieldset class="form-grid"><legend class="sr-only">Portal ${i + 1}</legend>
        ${field(`Portal ${i + 1}`, textControl({ id: `pp-${i}-label`, value: portal(i)?.label ?? '', maxlength: 80, placeholder: 'Portal de agentes' }))}
        ${field('Dirección', textControl({ id: `pp-${i}-url`, type: 'url', value: portal(i)?.url ?? '', maxlength: 300, placeholder: 'https://' }))}
      </fieldset>`,
      )
      .join('')}
    <div class="actions-row"><button type="button" class="btn" data-profile-cancel>Cancelar</button><button type="submit" class="btn primary" id="carrier-profile-save">Guardar ficha</button></div>
  </form>`;
}

export function profileView(p: CarrierProfile) {
  const edit =
    p.editable && editing !== p.carrier
      ? `<button type="button" class="btn sm" data-profile-edit="${esc(p.carrier)}">${icon('sliders')}Editar ficha</button>`
      : '';
  const body =
    editing === p.carrier && p.editable
      ? profileForm(p)
      : `<div class="profile-grid">
      <div><h3 class="sub">Contactos</h3>${contactsView(p.contacts, p.editable)}</div>
      <div><h3 class="sub">Portales</h3>${
        p.portals.length
          ? `<ul class="portals">${p.portals
              .map(
                (x) =>
                  `<li><a href="${esc(x.url)}" target="_blank" rel="noopener noreferrer">${esc(x.label)}</a> <span class="muted small">${esc(x.url)}</span></li>`,
              )
              .join('')}</ul>`
          : '<p class="muted small">Sin portales registrados.</p>'
      }</div>
      ${'remittanceAccount' in p ? `<div><h3 class="sub">Cuenta para remesas</h3>${accountView(p.remittanceAccount ?? null)}</div>` : ''}
    </div>
    ${p.agreement ? `<h3 class="sub">Convenio de comisión vigente</h3>${agreementView(p.agreement, p.carrier)}<p class="muted small">Lo administra Nubi con la agencia; aquí solo se consulta.</p>` : ''}`;
  return `<section class="card carrier-profile" id="ficha-${esc(p.carrier)}" aria-labelledby="ficha-${esc(p.carrier)}-title" data-component="carrier-profile">
    <div class="card-head"><h2 id="ficha-${esc(p.carrier)}-title">Ficha de ${esc(p.carrierName)}</h2><span class="spacer"></span>${edit}</div>
    <dl class="plan-meta small">
      <div><dt>Código de agente de la agencia</dt><dd class="mono">${p.agentCode ? esc(p.agentCode) : '<span class="muted">Sin código</span>'}</dd></div>
      <div><dt>Catálogo de planes</dt><dd>${p.catalog ? `versión <span class="mono">${esc(p.catalog.version)}</span>` : '<span class="muted">La aseguradora no declara catálogo</span>'}</dd></div>
      ${p.updatedAt ? `<div><dt>Ficha actualizada</dt><dd>${esc(date(p.updatedAt))}</dd></div>` : ''}
    </dl>
    ${body}
  </section>`;
}

/** The profile of each carrier the agency works with. */
export async function profilesSection(): Promise<string> {
  const carriers = Object.keys(carrierNames);
  const cards = await Promise.all(
    carriers.map((c) =>
      api<CarrierProfile>('GET', `/v1/carriers/${encodeURIComponent(c)}/profile`)
        .then(profileView)
        .catch(
          () =>
            `<section class="card carrier-profile" data-component="carrier-profile"><div class="card-head"><h2>Ficha de ${esc(carrierNames[c] ?? c)}</h2></div><p class="note bad">${icon('alert')}<span>No se pudo cargar la ficha.</span></p><button type="button" class="btn" data-plan-retry>Reintentar</button></section>`,
        ),
    ),
  );
  return cards.join('');
}

// ------------------------------------------------------------------ reading the forms

const valueOf = (id: string) =>
  ((document.getElementById(id) as HTMLInputElement | null)?.value ?? '').trim();

/** The profile as typed: an area without a name is left out; so is a portal without an address. */
export function profileInput(withAccount: boolean) {
  const contacts = Object.keys(AREA).flatMap((area) => {
    const name = valueOf(`pc-${area}-name`);
    const email = valueOf(`pc-${area}-email`);
    const phone = valueOf(`pc-${area}-phone`);
    if (!name && (email || phone))
      throw new InputError(
        `Falta el nombre del contacto de ${AREA[area]?.toLowerCase()}.`,
        `pc-${area}-name`,
      );
    return name ? [{ area, name, email: email || null, phone: phone || null }] : [];
  });
  const portals = [0, 1, 2].flatMap((i) => {
    const label = valueOf(`pp-${i}-label`);
    const url = valueOf(`pp-${i}-url`);
    if (!url) return [];
    if (!/^https:\/\//.test(url))
      throw new InputError('La dirección del portal debe empezar con https://', `pp-${i}-url`);
    return [{ label: label || 'Portal', url }];
  });
  const out: Record<string, unknown> = { contacts, portals };
  if (withAccount) {
    const bank = valueOf('pa-bank');
    const number = valueOf('pa-number');
    if (bank || number) {
      if (!/^[0-9][0-9-]{3,29}$/.test(number))
        throw new InputError('El número de cuenta lleva solo dígitos y guiones.', 'pa-number');
      out.remittanceAccount = {
        bank,
        holder: valueOf('pa-holder'),
        number,
        type: valueOf('pa-type') || 'MONETARY',
        currency: valueOf('pa-currency') || 'GTQ',
      };
    } else out.remittanceAccount = null;
  }
  return out;
}

const repaint = () => render(current, { quiet: true });

export const actions: Actions = {
  click: {
    planLine: (_t, d) => {
      line = d.planLine ?? line;
      void repaint();
    },
    planOpen: (t, d) => {
      const key = d.planOpen ?? '';
      if (detail?.key === key) {
        detail = null;
        void repaint();
        return;
      }
      void act(t, 'Abriendo el plan…', async () => {
        detail = await api<PlanDetail>('GET', `/v1/catalog/plans/${pathOf(key)}`);
        await repaint();
        $('#plan-detail')?.focus();
        $('#plan-detail')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return `${detail.displayName}: ${detail.coverages.length} coberturas.`;
      });
    },
    planClose: () => {
      detail = null;
      void repaint();
    },
    planCompare: (t) => {
      if (picked.length < 2) {
        toast('Elige de 2 a 4 planes del mismo ramo para compararlos.', 'error');
        return;
      }
      void act(t, 'Comparando los planes…', async () => {
        comparison = await api<Comparison>(
          'GET',
          `/v1/catalog/compare?plans=${encodeURIComponent(picked.join(','))}`,
        );
        await repaint();
        $('#plan-compare')?.focus();
        $('#plan-compare')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return `${comparison.plans.length} planes lado a lado.`;
      });
    },
    planCompareClose: () => {
      comparison = null;
      void repaint();
    },
    planRetry: () => {
      void repaint();
    },
    profileEdit: (_t, d) => {
      editing = d.profileEdit ?? null;
      void repaint();
    },
    profileCancel: () => {
      editing = null;
      void repaint();
    },
  },
  submit: {
    'plan-settings-form': (ev) => {
      ev.preventDefault();
      const key = (ev.target as HTMLElement).dataset.key ?? '';
      void act($('#plan-settings-save'), 'Guardando los ajustes…', async () => {
        detail = await api<PlanDetail>('PUT', `/v1/catalog/plans/${pathOf(key)}/settings`, {
          commercialName: valueOf('plan-commercial') || null,
          note: valueOf('plan-note') || null,
          hidden: !!(document.getElementById('plan-hidden') as HTMLInputElement | null)?.checked,
        });
        // «Cotizar» names the plan as the agency now does.
        setPlanNames(
          await api<PlanNames>('GET', '/v1/catalog/plan-names').catch(() => ({}) as PlanNames),
        );
        await repaint();
        return detail.hidden
          ? `${detail.displayName} guardado y oculto a los agentes.`
          : `${detail.displayName} guardado.`;
      });
    },
    'carrier-profile-form': (ev) => {
      ev.preventDefault();
      const form = ev.target as HTMLElement;
      const carrier = form.dataset.carrier ?? '';
      void act($('#carrier-profile-save'), 'Guardando la ficha…', async () => {
        const body = profileInput(!!document.getElementById('pa-bank'));
        await api('PUT', `/v1/carriers/${encodeURIComponent(carrier)}/profile`, body);
        editing = null;
        await repaint();
        return `Ficha de ${carrierNames[carrier] ?? carrier} guardada.`;
      });
    },
  },
  changeData: {
    planPick: (control) => {
      const key = control.dataset.planPick ?? '';
      const on = (control as unknown as HTMLInputElement).checked;
      picked = on ? [...new Set([...picked, key])].slice(0, 4) : picked.filter((k) => k !== key);
      // Plans of another product line are not comparable: ticking one starts a new choice.
      const lineOf = (k: string) => k.split(':')[1];
      if (on) picked = picked.filter((k) => lineOf(k) === lineOf(key));
      void repaint();
    },
  },
};
